import { useEffect, useRef, useCallback } from "react";
import { saveAssignmentProgress } from "../utils/assignments/saveAssignmentProgress";

const AUTOSAVE_INTERVAL_MS = 60_000;

export default function useAssignmentAutosave({
  user,
  packet,
  questions,
  answers,
  marked,
  questionTimes,
  currentIndex,
  timeRemaining,
  assignmentId,
  disabled = false,
}) {
  // ===== REFS (SOURCE OF TRUTH) =====
  const answersRef = useRef(answers);
  const markedRef = useRef(marked);
  const questionTimesRef = useRef(questionTimes);
  const currentIndexRef = useRef(currentIndex);
  const timeRemainingRef = useRef(timeRemaining);
  const questionStartRef = useRef(Date.now());

  // ===== KEEP REFS SYNCED =====
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    markedRef.current = marked;
  }, [marked]);

  useEffect(() => {
    questionTimesRef.current = questionTimes;
  }, [questionTimes]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
    questionStartRef.current = Date.now();
  }, [currentIndex]);

  useEffect(() => {
    timeRemainingRef.current = timeRemaining;
  }, [timeRemaining]);

  // ===== FLUSH CURRENT QUESTION TIME =====
  const flushCurrentQuestionTime = () => {
    const idx = currentIndexRef.current;
    const q = questions?.[idx];
    if (!q?.id) return;

    const elapsed = Math.round((Date.now() - questionStartRef.current) / 1000);

    questionTimesRef.current = {
      ...questionTimesRef.current,
      [q.id]: (questionTimesRef.current[q.id] ?? 0) + elapsed,
    };

    questionStartRef.current = Date.now();
  };

  // ===== SAVE NOW =====
  const saveNow = useCallback(
    async (overrides = {}) => {
      if (!user || !assignmentId || !questions?.length) {
        return false;
      }

      if (disabled) return false;

      flushCurrentQuestionTime();

      const estimatedMinutes =
        packet?.estimatedMinutes ?? packet?.estimatedTimeMin ?? 0;

      const totalTimeSpentSeconds =
        estimatedMinutes * 60 - (timeRemainingRef.current ?? 0);

      const questionProgress = Object.fromEntries(
        questions.map((q, i) => [
          q.id,
          {
            answer: answersRef.current?.[i] ?? null,
            timeSpentSeconds: questionTimesRef.current?.[q.id] ?? 0,
            marked: !!markedRef.current?.[i],
          },
        ])
      );

      await saveAssignmentProgress({
        studentUid: user.uid,
        packetId: assignmentId,
        status: overrides.status ?? "in_progress",
        currentQuestionIndex: currentIndexRef.current,
        totalTimeSpentSeconds,
        questionProgress,
        markedQuestionIds: questions
          .map((q, i) => (markedRef.current?.[i] ? q.id : null))
          .filter(Boolean),

        // ✅ THESE WERE MISSING
        correct: overrides.correct,
        completedAt: overrides.completedAt,
      });

      return true;
    },
    [user, packet, questions, assignmentId, disabled]
  );

  // ===== AUTOSAVE =====
  useEffect(() => {
    if (
      disabled ||
      !user ||
      !packet ||
      !questions ||
      questions.length === 0
    ) {
      return;
    }

    const interval = setInterval(() => {
      saveNow();
    }, AUTOSAVE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [disabled, user, packet, questions, saveNow]);

  return { saveNow };
}
